/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/react
 * @description Renders the single-domain adapter detail view for the React
 * variant. The page surfaces current provider health, available providers, and
 * inline configuration for one adapter domain.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useId, useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterDomainIcon } from "../components/AdapterDomainIcon";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapterStatus, useAdapters } from "../hooks";
import { cn } from "../lib/utils";
import { PageErrorBoundary } from "./PageErrorBoundary";

/**
 * Props for rendering the React single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property onConfigure Optional callback invoked with submitted adapter configuration.
 * @property className Optional class name merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  className?: string;
}

/**
 * Renders the React adapter detail page for one adapter domain.
 *
 * @param props Page props including the selected domain and optional back-navigation handler.
 * @returns The detail view with provider health, available providers, and configuration affordances.
 */
export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  return (
    <PageErrorBoundary
      className={props.className}
      title={`${DOMAIN_LABELS[props.domain]} adapter failed to load`}
    >
      <AdapterDetailPageContent {...props} />
    </PageErrorBoundary>
  );
}

function AdapterDetailPageContent({
  domain,
  onBack,
  onConfigure,
  className,
}: AdapterDetailPageProps): JSX.Element {
  const { isLoading } = useAdapters();
  const status = useAdapterStatus(domain);
  const [isEditing, setIsEditing] = useState(false);
  const providers = getProvidersForDomain(domain);
  // Mirror AdapterConfigForm's selectable filter so the detail page surfaces
  // which DB providers are actually wirable today vs. deferred/BYOD. The
  // chooser hides them; the detail page shows them with an "Unavailable"
  // tag so consumers learn the launch axis without losing discovery.
  const launchSelectableIds = new Set(
    domain === "db"
      ? ["localStorage", "convex", "neon", "cloudflareKV", "memory"]
      : providers.map((p) => p.id),
  );
  const latencyLabelId = useId();

  if (isLoading) {
    return (
      <div className={cn("mx-auto max-w-3xl space-y-6", className)}>
        <div
          aria-label={`Loading ${DOMAIN_LABELS[domain]} adapter`}
          aria-live="polite"
          className="animate-pulse space-y-4 motion-reduce:animate-none"
          role="status"
        >
          <div className="h-6 w-32 rounded bg-muted" />
          <div className="h-40 rounded-lg border border-border bg-card" />
          <div className="h-60 rounded-lg border border-border bg-card" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("mx-auto max-w-3xl space-y-8 text-foreground", className)}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          <span>←</span> Back to Adapters
        </button>
      )}

      <div className="flex items-start gap-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-border bg-card text-3xl shadow-sm">
          <AdapterDomainIcon className="h-8 w-8" domain={domain} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold text-foreground">
              {DOMAIN_LABELS[domain]}
            </h1>
            <AdapterStatusBadge status={status.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {DOMAIN_DESCRIPTIONS[domain]}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
        <h2 className="mb-4 text-xs font-semibold uppercase text-muted-foreground">
          Current Status
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-[0.625rem] uppercase text-muted-foreground">
              Provider
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground">
              {status.provider !== "none" ? status.provider : "None"}
            </p>
          </div>
          <div>
            <p className="text-[0.625rem] uppercase text-muted-foreground">
              Status
            </p>
            <p className="mt-0.5">
              <AdapterStatusBadge status={status.status} />
            </p>
          </div>
          <div>
            <p className="text-[0.625rem] uppercase text-muted-foreground">
              Last Checked
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {status.lastCheckedAt
                ? new Date(status.lastCheckedAt).toLocaleTimeString()
                : "Not checked"}
            </p>
          </div>
        </div>

        {status.error && (
          <div
            className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            role="alert"
          >
            <p>
              <span className="font-semibold">Error:</span> {status.error}
            </p>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="mt-3 rounded-md border border-destructive/30 bg-background px-3 py-1.5 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              Review configuration
            </button>
          </div>
        )}

        {status.latency !== undefined && (
          <div className="mt-4">
            <p
              id={latencyLabelId}
              className="mb-1 text-[0.625rem] uppercase text-muted-foreground"
            >
              Latency
            </p>
            <meter
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
              aria-labelledby={latencyLabelId}
              min={0}
              max={1000}
              value={Math.min(1000, status.latency)}
              aria-valuetext={`${status.latency} milliseconds`}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {status.latency}ms
            </p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase text-muted-foreground">
            Available Providers
          </h2>
          <span className="text-xs text-muted-foreground">
            {providers.length} providers
          </span>
        </div>
        <div className="divide-y divide-border">
          {providers.map((provider) => {
            const isAvailable = launchSelectableIds.has(provider.id);
            return (
              <div
                key={provider.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                data-available={isAvailable ? "true" : "false"}
              >
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {provider.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {provider.description}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span
                    className={cn(
                      "self-start rounded px-2 py-0.5 text-[0.625rem] font-bold uppercase",
                      provider.tier === "pronto"
                        ? "bg-primary/10 text-primary"
                        : provider.tier === "lancio"
                          ? "bg-accent/10 text-accent"
                          : "bg-muted text-muted-foreground",
                    )}
                  >
                    {provider.tier}
                  </span>
                  {!isAvailable && (
                    <span className="self-start rounded border border-border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-muted-foreground">
                      Unavailable
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {isEditing ? (
          <AdapterConfigForm
            domain={domain}
            onSave={async (config) => {
              await onConfigure?.(domain, config);
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="w-full rounded-lg border border-dashed border-border bg-card/40 p-6 text-center text-sm text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground active:border-primary/40 active:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Configure {DOMAIN_LABELS[domain]} Adapter
          </button>
        )}
      </div>
    </div>
  );
}
